import { supabase } from '@/lib/supabaseClient';
import type { FavoriteEntry } from '@/types/favorites';

import { getSignedImageUrls } from './storage';

interface RawFavoriteImage {
  id: string;
  storage_path: string;
  generation_id: string;
  generations: { collection_id: string } | null;
}

interface RawFavoriteRow {
  id: string;
  created_at: string;
  generation_images: RawFavoriteImage | null;
}

/** Уникальный constraint (user_id, generation_image_id) в Postgres. */
const UNIQUE_VIOLATION = '23505';

export async function fetchFavorites(): Promise<FavoriteEntry[]> {
  if (!supabase) {
    return [];
  }

  const { data, error } = await supabase
    .from('favorites')
    .select(
      'id, created_at, generation_images(id, storage_path, generation_id, generations(collection_id))',
    )
    .order('created_at', { ascending: false })
    .returns<RawFavoriteRow[]>();

  if (error) {
    throw new Error('Не удалось загрузить избранное.');
  }

  const rows = (data ?? []).filter(
    (row): row is RawFavoriteRow & { generation_images: RawFavoriteImage } =>
      row.generation_images !== null,
  );

  const signedUrls = await getSignedImageUrls(rows.map((row) => row.generation_images.storage_path));

  return rows.map((row) => ({
    favoriteId: row.id,
    generationImageId: row.generation_images.id,
    generationId: row.generation_images.generation_id,
    collectionId: row.generation_images.generations?.collection_id ?? '',
    createdAt: row.created_at,
    imageUrl: signedUrls.get(row.generation_images.storage_path) ?? null,
  }));
}

export async function addFavorite(userId: string, generationImageId: string): Promise<void> {
  if (!supabase) {
    return;
  }

  const { error } = await supabase
    .from('favorites')
    .insert({ user_id: userId, generation_image_id: generationImageId });

  if (error && error.code !== UNIQUE_VIOLATION) {
    throw new Error('Не удалось добавить в избранное.');
  }
}

export async function removeFavorite(favoriteId: string): Promise<void> {
  if (!supabase) {
    return;
  }

  const { error } = await supabase.from('favorites').delete().eq('id', favoriteId);

  if (error) {
    throw new Error('Не удалось убрать из избранного.');
  }
}
